import type { ItineraryItem } from '@elara/client-core/models/ItineraryItemModel';
import type { FlightSegment } from '@elara/domain/models/schemas/flight-schemas';

import type { BaseItineraryBlockProps, ItineraryItemAction } from './BaseItineraryBlock.js';

import React from 'react';

import { cn } from '@elara/client-core/lib/utils';
import { createFlightSegmentModel } from '@elara/client-core/models/FlightSegmentModel';
import { bookingDisplayStatusLabel } from '@elara/domain/booking-status';

import { bookingStatusIcon, bookingStatusLabelColor } from '#lib/booking-status.js';
import { useCanRefreshPricing } from '#lib/can-refresh-pricing.js';
import { useCopy } from '#lib/use-copy.js';
import { useRefreshPricing } from '#lib/useRefreshPricing.js';
import { useItineraryState, useNavigate, useTripsState } from '#states/hooks.js';
import { AirlineLogo } from '#ui/cards/FlightCard.js';
import { GoogleMap } from '#ui/GoogleMap.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import { PriceStatusBanner } from '#ui/PriceStatusBanner.js';
import { isBookable } from '#views/trip/bookings/booking-grouping.js';

import { BaseItineraryBlock } from './BaseItineraryBlock.js';
import { FlightTimeline } from './FlightTimeline.js';
import { ItemNotesDialog } from './ItemNotesDialog.js';
import { bookingDetailActions, viewBookingDetailsAction } from './itinerary-booking-actions.js';
import { DetailRow } from './ItineraryDetailRow.js';
import { ItineraryNotesSection } from './ItineraryNotesSection.js';
import { PrintBookingDetails } from './PrintBookingDetails.js';

// SVG wrapper components
function PlaneIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="86"
      height="87"
      viewBox="0 0 86 87"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4.52435 26.8673H0L4.45562 40.2336C4.45562 40.2336 0.0721267 41.7887 0.000284576 43.2747C0.000284576 44.6889 4.45562 46.3158 4.45562 46.3158L0.0693663 59.6073L4.59372 59.6073L11.9496 47.727C11.9496 47.727 22.9788 49.0004 38.7475 49.2134L28.3502 86.3365L34.2888 86.3365L47.655 62.5074L54.4443 62.5074C55.7149 62.5074 56.7065 61.5158 56.7065 60.2452C56.7065 58.9747 55.7149 57.983 54.4443 57.983H50.2017L55.1514 49.0724H72.9733C72.9733 49.0724 80.8234 49.0005 85.0661 43.0618C80.8235 37.1232 72.9733 37.0513 72.9733 37.0513L55.0828 37.2641L50.1331 28.3534H54.3757C55.6463 28.3534 56.6379 27.3618 56.6379 26.0913C56.6379 24.8207 55.6463 23.829 54.3757 23.8291L47.5864 23.8291L34.2202 0L28.2817 6.7435e-07L38.679 37.1231C22.9098 37.3359 11.881 38.6094 11.881 38.6094L4.52435 26.8673Z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * One [time][icon][label] row of the flight timeline — the `w-14` time column and `gap-3` match
 * `BaseItineraryBlock`'s leading time chip exactly, so a flight's own departure/arrival times land
 * in the same left column every other event kind's time chip occupies (mirrors the mobile app's
 * flight row). A row with nothing for a column just leaves it empty rather than skipping it, so the
 * icon/label stay aligned across rows.
 */
function FlightTimelineRow({
  time,
  icon,
  connector,
  children,
}: {
  time?: React.ReactNode;
  icon?: React.ReactNode;
  /** Stretches the icon column to the row's full height instead of centering a fixed-size glyph —
   * for the travel-time row's connector line between the takeoff and landing icons. */
  connector?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-18 flex-shrink-0 text-xs tabular-nums text-muted-foreground mr-1">{time}</div>
      <div className={cn('w-4 flex-shrink-0 flex items-center justify-center', connector && 'self-stretch')}>
        {icon}
      </div>
      <div className="flex-1 min-w-0 overflow-hidden">{children}</div>
    </div>
  );
}

/**
 * Condensed departure → arrival → airline timeline for the itinerary builder's collapsed row —
 * mirrors the mobile app's flight row: takeoff/landing icons connected by a line, then the airline
 * and flight number on their own row. Booking status stays in the header's Book button/status
 * label, so it isn't repeated here.
 */
function FlightRowTimeline({
  segment,
  airline,
}: {
  segment: ReturnType<typeof createFlightSegmentModel>;
  airline: FlightSegment['airline'];
}) {
  return (
    <div className="flex flex-col gap-1 text-sm">
      <FlightTimelineRow
        time={segment.formattedDepartureTime}
        icon={<MaterialIcon icon="flight_takeoff" size={IconSizes.xs} className="text-muted-foreground" />}
      >
        <span className="truncate font-medium">{segment.origin.airport_name}</span>
      </FlightTimelineRow>

      {segment.formattedDuration && (
        <FlightTimelineRow connector icon={<div className="w-px h-full min-h-3 py-4 border-l border-input" />}>
          <span className="text-2xs text-muted-foreground">{segment.formattedDuration} travel time</span>
        </FlightTimelineRow>
      )}

      <FlightTimelineRow
        time={
          <>
            {segment.formattedArrivalTime}
            {segment.isOvernight && <sup className="text-xxs font-normal"> +1</sup>}
          </>
        }
        icon={<MaterialIcon icon="flight_land" size={IconSizes.xs} className="text-muted-foreground" />}
      >
        <span className="truncate font-medium">{segment.destination.airport_name}</span>
      </FlightTimelineRow>

      <FlightTimelineRow icon={<AirlineLogo airline={airline} className="h-4 w-4" />}>
        {/* Separate, gap-spaced attributes with no bullet separator — matches how the hotel row
            shows its "3 nights" / "1 room" attributes. */}
        <div className="flex items-center flex-wrap gap-3 text-xs text-muted-foreground">
          {segment.airlineName && <span className="truncate">{segment.airlineName}</span>}
          {segment.flightNumber && <span>{segment.flightNumber}</span>}
        </div>
      </FlightTimelineRow>
    </div>
  );
}

/**
 * The expanded detail for a single flight leg in the full-page itinerary: the timeline, then cabin,
 * aircraft, operator and booking info as the same Google-Maps-style icon rows the hotel and place
 * blocks use.
 */
function FlightLegBody({
  segment,
  item,
  onShowGuide,
}: {
  segment: ReturnType<typeof createFlightSegmentModel>;
  item: ItineraryItem;
  onShowGuide?: () => void;
}) {
  const copy = useCopy();
  const bookingStatus = item.booking?.status;
  const confirmationNumber = item.booking?.confirmationNumber;
  const hasRows = Boolean(
    segment.flightNumber ||
    segment.travelClass ||
    segment.aircraftType ||
    segment.operatedBy ||
    bookingStatus ||
    confirmationNumber ||
    onShowGuide,
  );

  return (
    <>
      <FlightTimeline segment={segment} />

      {hasRows && (
        <div className="flex flex-col text-sm">
          {segment.flightNumber && (
            <DetailRow
              icon="airplane_ticket"
              trailingIcon="content_copy"
              value={segment.flightNumber}
              onClick={() => copy(segment.flightNumber!, 'Flight number copied')}
              tooltip="Copy flight number"
            />
          )}
          {segment.travelClass && (
            <DetailRow
              icon="airline_seat_recline_normal"
              value={<span className="capitalize">{segment.travelClass}</span>}
            />
          )}
          {segment.aircraftType && <DetailRow icon="flight" value={segment.aircraftType} />}
          {segment.operatedBy && <DetailRow icon="corporate_fare" value={`Operated by ${segment.operatedBy}`} />}
          {bookingStatus && (
            <DetailRow
              icon={bookingStatusIcon(bookingStatus)}
              iconClassName={bookingStatusLabelColor(bookingStatus)}
              value={
                <span className={bookingStatusLabelColor(bookingStatus)}>
                  {bookingDisplayStatusLabel(bookingStatus)}
                </span>
              }
            />
          )}
          {confirmationNumber && (
            <DetailRow
              icon="confirmation_number"
              trailingIcon="content_copy"
              value={confirmationNumber}
              secondary="Confirmation number"
              onClick={() => copy(confirmationNumber, 'Confirmation number copied')}
              tooltip="Copy confirmation number"
            />
          )}
          {onShowGuide && (
            <DetailRow
              className="print:hidden"
              icon="more_horiz"
              value="View full flight details"
              onClick={onShowGuide}
            />
          )}
        </div>
      )}
      {/* Booking reference, provider and lead guest on paper (the confirmation number is a row above). */}
      <PrintBookingDetails item={item} includeConfirmation={false} />
    </>
  );
}

/**
 * A flight leg on the itinerary builder card: the visual departure → arrival timeline, then the
 * leg's metadata (airline, cabin, aircraft, operator) as Google-Maps-style icon rows.
 */
function FlightLegRows({
  segment,
  cost,
  costDetail,
  customerRequest,
  advisorNote,
  notes,
  onEditNote,
  onShowGuide,
}: {
  segment: ReturnType<typeof createFlightSegmentModel>;
  cost?: string | null;
  costDetail?: string | null;
  customerRequest?: string | null;
  advisorNote?: string | null;
  notes?: string | null;
  onEditNote?: () => void;
  onShowGuide?: () => void;
}) {
  const copy = useCopy();

  return (
    <div className="@container flex flex-col gap-4 text-sm">
      <div className="flex flex-col">
        {cost && <DetailRow icon="payments" value={cost} secondary={costDetail ?? undefined} />}
        {segment.flightNumber && (
          <DetailRow
            icon="airplane_ticket"
            trailingIcon="content_copy"
            value={segment.flightNumber}
            onClick={() => copy(segment.flightNumber!, 'Flight number copied')}
            tooltip="Copy flight number"
          />
        )}
        {segment.travelClass && (
          <DetailRow
            icon="airline_seat_recline_normal"
            value={<span className="capitalize">{segment.travelClass}</span>}
          />
        )}
        {segment.aircraftType && <DetailRow icon="flight" value={segment.aircraftType} />}
        {segment.operatedBy && <DetailRow icon="corporate_fare" value={`Operated by ${segment.operatedBy}`} />}
        {/* What the fare includes (Wi-Fi, legroom, power, emissions, …) — each with its mapped icon. */}
        {segment.includes?.map((amenity) => (
          <DetailRow key={amenity} icon={segment.getAmenityIcon(amenity)} value={amenity} />
        ))}
        {customerRequest && <DetailRow icon="chat_bubble" value={customerRequest} />}
        {onShowGuide && (
          <DetailRow
            className="print:hidden"
            icon="more_horiz"
            value="View full flight details"
            onClick={onShowGuide}
          />
        )}
      </div>

      <ItineraryNotesSection notes={notes} advisorNote={advisorNote} onEditNote={onEditNote} />
    </div>
  );
}

export function FlightDepartureBlock({ event, items, size = 'default', className }: BaseItineraryBlockProps) {
  const tripsState = useTripsState();
  const itineraryState = useItineraryState();
  const { navigate } = useNavigate();
  const tripId = tripsState.current?.id;
  const [isNotesDialogOpen, setIsNotesDialogOpen] = React.useState(false);

  const primaryFlightOption = items[0]?.type === 'flight' ? items[0].flightOption : undefined;
  const canRefreshEnabled = useCanRefreshPricing('flight', primaryFlightOption ?? undefined);
  const {
    canRefresh,
    isRefreshing,
    refresh: handleRefresh,
  } = useRefreshPricing({
    type: 'flight',
    referenceId: items[0]?.referenceId ?? undefined,
    enabled: canRefreshEnabled,
    label: items[0]?.displayTitle,
  });

  if (items.length === 0) return null;
  const primaryItem = items[0]!;

  if (event.type !== 'flight_departure' || primaryItem.type !== 'flight') return null;
  const segmentData = event.metadata?.segment as FlightSegment;

  if (!segmentData || !segmentData.origin || !segmentData.destination) return null;

  const segment = createFlightSegmentModel(segmentData);

  const isUnavailable = primaryItem.flightOption?.isAvailable === false;

  // The freshness banner is only relevant while the fare is still live (not yet booked/cancelled).
  const showPriceBanner = !primaryItem.isBooked && !primaryItem.isApproved && !primaryItem.isCancelled && event.cost;

  // The flight guide renders Elara's searched flight; one the user booked elsewhere has no guide to
  // open, so the affordance is left out rather than rendered dead.
  const guideFlightId = primaryItem.referenceId ?? undefined;

  // The full-page block links the guide as its "View full flight details" row instead of a button.
  const actions: ItineraryItemAction[] = [
    ...(guideFlightId && size !== 'lg'
      ? [
          {
            type: 'primary' as const,
            label: 'Flight details',
            icon: 'flight_takeoff' as const,
            onClick: () => tripsState.showGuide('flight', guideFlightId),
          },
        ]
      : []),
    ...(canRefresh && size !== 'lg'
      ? [
          {
            type: 'tertiary' as const,
            label: isRefreshing ? 'Refreshing...' : 'Refresh pricing',
            icon: 'refresh' as const,
            onClick: async (e: React.MouseEvent) => handleRefresh(e),
          },
        ]
      : []),
  ];

  const handleClick = guideFlightId ? () => tripsState.showGuide('flight', guideFlightId) : undefined;

  // "Book" jumps straight to the flight checkout (mirrors the bookings tab's row action).
  const bookFlight = (): void => {
    if (!tripId || !primaryItem.referenceId) return;
    navigate(`/checkout/flight/${tripId}/${primaryItem.referenceId}`);
  };

  // Expanded builder card: guide + Remove, with refresh in the overflow.
  // Booking is handled by the header "Book" button (headerBookAction).
  const defaultActions: ItineraryItemAction[] = [
    ...viewBookingDetailsAction(primaryItem, (item) => itineraryState.openViewBookingDialog(item)),
    // "Add booking" / "Edit booking" records a flight booked elsewhere.
    ...bookingDetailActions(primaryItem, itineraryState, { add: 'secondary' }),
    // Refresh pricing isn't repeated here — the PriceStatusBanner above carries the recheck affordance.
    {
      type: 'secondary',
      label: 'Remove',
      icon: 'delete',
      destructive: true,
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        itineraryState.openDeleteDialog(primaryItem);
      },
    },
  ];

  // A still-planned unavailable flight surfaces "Sold out" as its booking status. Once booked or
  // approved, that booking status wins — the search option's availability is no longer relevant.
  const bookingStatusOverride = isUnavailable && primaryItem.isPlanned ? ('unavailable' as const) : undefined;

  // Every still-bookable leg of a multi-segment flight offers its own "Book" action — each leg is
  // its own itinerary item, so nothing stops booking one independently.
  const headerBookAction =
    isBookable(primaryItem) && guideFlightId ? { label: 'Book', onClick: bookFlight } : undefined;

  if (size === 'sm') {
    return (
      <BaseItineraryBlock
        event={event}
        items={items}
        title={event.title}
        canDeleteItem={true}
        actions={actions}
        bookingStatusOverride={bookingStatusOverride}
        headerBookAction={headerBookAction}
        size={size}
        className={className}
        onClick={handleClick}
      />
    );
  }

  if (size === 'default') {
    return (
      <>
        <BaseItineraryBlock
          event={event}
          title={event.title}
          items={items}
          canDeleteItem={false}
          actions={defaultActions}
          bookingStatusOverride={bookingStatusOverride}
          headerBookAction={headerBookAction}
          size={size}
          className={className}
          onClick={handleClick}
          // Each `FlightTimelineRow` carries its own `w-18` time chip, so the shared column is omitted
          // rather than reserved — the legs' times already sit in that column.
          ownsTimeColumn
          preHeaderContent={<FlightRowTimeline segment={segment} airline={segmentData.airline} />}
          summary={
            <div className="rounded-sm overflow-hidden h-52">
              <GoogleMap
                points={segment.mapPoints}
                showPolyline={true}
                polylineOptions={{ strokeColor: '#d08700', strokeWeight: 2, strokeOpacity: 0.8, geodesic: true }}
                className="h-full w-full"
                interactive={false}
              />
            </div>
          }
        >
          {primaryFlightOption && showPriceBanner && (
            <PriceStatusBanner
              type="flight"
              model={primaryFlightOption}
              priceDivisor={primaryFlightOption.totalPaidPassengers}
              priceUnit="per passenger"
              size="sm"
            />
          )}
          <FlightLegRows
            segment={segment}
            cost={primaryItem.formattedCost}
            costDetail={primaryItem.formattedCostDetail}
            customerRequest={primaryItem.booking?.customerRequest}
            advisorNote={primaryItem.booking?.advisorNote}
            notes={primaryItem.notes}
            onEditNote={() => setIsNotesDialogOpen(true)}
            onShowGuide={primaryItem.referenceId ? handleClick : undefined}
          />
        </BaseItineraryBlock>

        {isNotesDialogOpen && (
          <ItemNotesDialog
            open={isNotesDialogOpen}
            onOpenChange={setIsNotesDialogOpen}
            initialNotes={primaryItem.notes}
            onConfirm={async (notes) => {
              await itineraryState.updateItemNotes(primaryItem.id, notes);
            }}
          />
        )}
      </>
    );
  }

  return (
    <BaseItineraryBlock
      event={event}
      items={items}
      canDeleteItem={true}
      actions={actions}
      bookingStatusOverride={bookingStatusOverride}
      headerBookAction={headerBookAction}
      size={size}
      className={className}
      onClick={handleClick}
      title={`Flight to ${segment.destination.city_name ?? segment.destination.airport_name}`}
      panelContent={
        <GoogleMap
          points={segment.mapPoints}
          showPolyline={true}
          polylineOptions={{ strokeColor: '#d08700', strokeWeight: 2, strokeOpacity: 0.8, geodesic: true }}
          className="h-full w-full"
          interactive={false}
        />
      }
      panelPosition="left"
    >
      <FlightLegBody segment={segment} item={primaryItem} onShowGuide={handleClick} />
    </BaseItineraryBlock>
  );
}
